import React, { useEffect, useState } from 'react';

import { Alert, Center, Loader, useMantineTheme } from '@mantine/core';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, StripeConstructorOptions, loadStripe } from '@stripe/stripe-js';
import { IconExclamationCircle } from '@tabler/icons-react';
import config from 'config';
import useAthlete from 'hooks/athlete/useAthlete';
import useSetupPaymentMethodIntent from 'hooks/paymentMethod/useSetupPaymentMethodIntent';
import { GymAthleteRetrieve } from 'types/generated/models/GymAthleteRetrieve';
import { GymRetrieve } from 'types/generated/models/GymRetrieve';

import StripePaymentMethodForm from './StripePaymentMethodForm';
import { IAutoPayOnAttach } from '../types';

interface IAddPaymentMethodPEProps {
  athleteId: string;
  gymId: string;
  gym: GymRetrieve | GymAthleteRetrieve;
  setAsDefault: boolean;
  /** Set only when the staff auto-pay toggle is on offer; undefined leaves the flow as it was. */
  autoPay?: IAutoPayOnAttach;
  cancelHandler: () => void;
  successHandler: (paymentMethodId?: string) => void;
}

const AddPaymentMethodPE = ({
  athleteId,
  gym,
  setAsDefault,
  autoPay,
  cancelHandler,
  successHandler,
}: IAddPaymentMethodPEProps): JSX.Element => {
  const theme = useMantineTheme();

  const [clientSecret, setClientSecret] = useState<string | undefined>();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | undefined>();
  const [state, setState] = useState<'loading' | 'error' | 'success'>('loading');

  const {
    data: athleteData,
    isLoading: isAthleteLoading,
    isSuccess: isAthleteSuccess,
    isError: isAthleteError,
  } = useAthlete(athleteId);

  const {
    data: setupIntentData,
    isLoading: isSetupIntentLoading,
    isSuccess: isSetupIntentSuccess,
    isError: isSetupIntentError,
  } = useSetupPaymentMethodIntent({
    athleteId: athleteId,
    enabled: !!athleteId,
  });

  const handleSuccess = (paymentMethodId: string): void => {
    setState('loading');
    successHandler(paymentMethodId);
  };

  useEffect(() => {
    if (isAthleteLoading || isSetupIntentLoading) {
      setState('loading');
      setClientSecret(undefined);
      setStripePromise(undefined);
    } else if (isAthleteError || isSetupIntentError) {
      setState('error');
      setClientSecret(undefined);
      setStripePromise(undefined);
    } else if (isAthleteSuccess && athleteData && isSetupIntentSuccess && setupIntentData) {
      setClientSecret(setupIntentData.data.clientSecret);
      const developerTools = {
        assistant: { enabled: false },
      };

      const stripeOptions: StripeConstructorOptions =
        gym.stripeAccountType == 'standard'
          ? {
              stripeAccount: gym.stripeConnectAccount || '',
              developerTools,
            }
          : { developerTools };
      setStripePromise(loadStripe(config.STRIPE_PUBLIC_KEY, stripeOptions));
      setState('success');
    }
  }, [
    isAthleteLoading,
    isAthleteSuccess,
    athleteData,
    isAthleteError,
    isSetupIntentLoading,
    isSetupIntentSuccess,
    isSetupIntentError,
    setupIntentData,
    gym,
  ]);

  return (
    <div>
      {state === 'error' && (
        <Center my='xl'>
          <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
            Something went wrong!
          </Alert>
        </Center>
      )}
      {state === 'loading' && (
        <Center my='xl'>
          <Loader />
        </Center>
      )}
      {state === 'success' && isAthleteSuccess && athleteData?.data && stripePromise && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <StripePaymentMethodForm
            athleteId={athleteId}
            athleteEmail={athleteData.data.email || ''}
            athleteFullName={athleteData.data.fullName || ''}
            athletePhone={athleteData.data.phone || ''}
            setAsDefault={setAsDefault}
            autoPay={autoPay}
            handleCancel={cancelHandler}
            handleSuccess={handleSuccess}
          />
        </Elements>
      )}
    </div>
  );
};

export default AddPaymentMethodPE;
